import { useRef } from "react";
import { ImagePlus, Inbox, Loader2, X } from "lucide-react";
import { compressImage } from "../lib/api";
import { toast } from "sonner";

export const PageHeader = ({ title, subtitle, actions }) => (
  <div className="flex flex-wrap items-end justify-between gap-4 mb-8 fade-up">
    <div>
      {subtitle && <p className="eyebrow mb-2">{subtitle}</p>}
      <h1 className="font-display text-3xl sm:text-4xl font-bold text-[var(--navy)]" data-testid="page-title">{title}</h1>
    </div>
    {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
  </div>
);

export const Panel = ({ children, className = "", ...p }) => (
  <div className={`panel ${className}`} {...p}>{children}</div>
);

export const Empty = ({ title, text, testid = "empty-state" }) => (
  <div className="panel flex flex-col items-center text-center py-12 px-6" data-testid={testid}>
    <Inbox className="w-10 h-10 text-[var(--teal)] mb-3" strokeWidth={1.5} />
    <p className="font-semibold text-[var(--navy)]">{title}</p>
    {text && <p className="text-sm text-slate-500 mt-1">{text}</p>}
  </div>
);

export const Loading = () => (
  <div className="flex items-center gap-2 text-slate-500 py-10" data-testid="loading"><Loader2 className="w-5 h-5 animate-spin" /> Caricamento...</div>
);

export const SevBadge = ({ sev }) => {
  const crit = sev === "critico" || sev === "CRITICO" || sev === "alta";
  return <span className={`tag ${crit ? "tag-red" : "tag-amber"}`}>{crit ? "CRITICO" : "ATTENZIONE"}</span>;
};

export const StatoBadge = ({ stato }) => {
  const cls = stato === "approvata" ? "tag-green" : stato === "rifiutata" ? "tag-red" : "tag-amber";
  return <span className={`tag ${cls}`}>{stato || "in attesa"}</span>;
};

export const Field = ({ label, children }) => (
  <label className="block space-y-1.5"><span className="text-sm font-medium text-slate-700">{label}</span>{children}</label>
);

export function ImagesInput({ value, onChange, max = 1, testid = "image-input" }) {
  const ref = useRef();
  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, max - value.length);
    try {
      const imgs = await Promise.all(files.map((f) => compressImage(f)));
      onChange([...value, ...imgs].slice(0, max));
    } catch { toast.error("Impossibile elaborare l'immagine"); }
    e.target.value = "";
  };
  return (
    <div className="flex flex-wrap gap-3">
      {value.map((src, i) => (
        <div key={i} className="relative w-24 h-24 rounded-xl overflow-hidden border">
          <img src={src} alt="allegato" className="w-full h-full object-cover" />
          <button type="button" onClick={() => onChange(value.filter((_, j) => j !== i))} className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-1" data-testid={`${testid}-remove-${i}`}><X className="w-3 h-3" /></button>
        </div>
      ))}
      {value.length < max && (
        <button type="button" onClick={() => ref.current.click()} className="w-24 h-24 rounded-xl border-2 border-dashed border-slate-300 flex flex-col items-center justify-center text-slate-500 hover:border-[var(--teal)] hover:text-[var(--teal)] transition-colors" data-testid={testid}>
          <ImagePlus className="w-6 h-6" /><span className="text-xs mt-1">Allega</span>
        </button>
      )}
      <input ref={ref} type="file" accept="image/*" multiple={max > 1} hidden onChange={onFiles} />
    </div>
  );
}
